import type { FriendsResponse } from "@monekineko/shared";
import { pluralize } from "@monekineko/shared";
import { Badge, Button, Card, EmptyState, Skeleton } from "@monekineko/ui-kit";
import { useEffect, useState } from "react";
import { api } from "../api.js";
import { haptic, shareLink } from "../telegram.js";
import styles from "./screens.module.css";

// Экран друзей: ссылка, награды и кто уже пришёл.

export function FriendsScreen({ onBack }: { onBack: () => void }) {
  const [data, setData] = useState<FriendsResponse | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    void api.friends().then(setData).catch(() => undefined);
  }, []);

  if (!data) {
    return (
      <div className={styles.screen}>
        <Skeleton height={120} />
        <Skeleton height={64} />
      </div>
    );
  }

  const copy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(data.referralLink);
      setCopied(true);
      haptic("light");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div className={styles.screen}>
      <div className={styles.topBar}>
        <Button variant="ghost" size="sm" onClick={onBack}>
          ← Назад
        </Button>
        <span className={styles.levelTitle}>Друзья</span>
        <span />
      </div>

      <Card title="Твоя ссылка" subtitle="Друг проходит первый уровень — награда приходит тебе">
        <code className={styles.link}>{data.referralLink}</code>
        <div className={styles.rewardRow}>
          <Badge>+{data.rewards.lives} жизней</Badge>
          <Badge tone="warning">+{data.rewards.hints} подсказки</Badge>
          <Badge tone="success">+{data.rewards.points} очков</Badge>
        </div>
        {data.unlockPerInvites > 0 && data.invitesToNextUnlock != null ? (
          <p className={styles.note}>
            Ещё {data.invitesToNextUnlock}{" "}
            {pluralize(data.invitesToNextUnlock, ["друг", "друга", "друзей"])} — и откроется уровень
            досрочно
          </p>
        ) : null}
        <div className={styles.rewardRow}>
          <Button onClick={() => shareLink(data.referralLink, data.shareText)}>Позвать друга</Button>
          <Button variant="secondary" onClick={() => void copy()}>
            {copied ? "Скопировано" : "Скопировать"}
          </Button>
        </div>
      </Card>

      {data.referrer ? <p className={styles.note}>Тебя пригласил: {data.referrer.name}</p> : null}

      <Card title={`Приглашено: ${data.invited.length}`}>
        {data.invited.length === 0 ? (
          <EmptyState title="Пока никого" description="Отправь ссылку — и здесь появятся имена" />
        ) : (
          <ul className={styles.list}>
            {data.invited.map((friend, index) => (
              <li key={index} className={styles.listRow}>
                <span>{friend.name}</span>
                <span>
                  {friend.qualified ? (
                    <Badge tone="success">играет</Badge>
                  ) : (
                    <Badge tone="neutral">ещё не начал</Badge>
                  )}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}
